import { IMessageBarStyles, IStyle } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { BoxShadow, FontSizes, FontWeights, Sizes } from '../../themes/styles';

export const messageBarStylesDefault = (theme: IH2OTheme): Partial<IMessageBarStyles> => ({
  root: {
    position: 'relative',
    borderRadius: Sizes.borderRadius,
    boxShadow: BoxShadow,
    '&.has-actions': {
      '.ms-MessageBar-text': {
        marginRight: 0,
        '@media(max-width: 768px)': {
          marginRight: 48,
          marginBottom: 50,
        },
      },
      '.ms-MessageBar-actionsSingleLine': {
        '@media(max-width: 768px)': {
          position: 'absolute',
          right: 0,
          bottom: 8,
        },
      },
    },
    '&.has-large-content-width': {
      '.ms-MessageBar-text': {
        marginRight: '24px !important',
        marginBottom: 50,
      },
      '.ms-MessageBar-actionsSingleLine': {
        position: 'absolute',
        right: 0,
        bottom: 8,
        marginRight: 18,
      },
    },
    '&.ms-MessageBar-multiline': {
      '.ms-MessageBar-actions': {
        marginRight: 45,
      },
    },
    '&.MessageBar-blocked': {
      '.ms-MessageBar-icon': {
        marginTop: 14,
      },
    },
    '&.has-dismissal-icon': {
      '.ms-MessageBar-actionsSingleLine': {
        marginRight: 48,
      },
    },
    '&.has-click-handler': {
      cursor: 'pointer',
    },
  },
  content: {
    position: 'relative',
  },
  iconContainer: {
    margin: '12px 0px 0px 24px',
    fontSize: FontSizes.xlarge,
  },
  text: {
    margin: '16px 48px 16px 24px',
  },
  innerText: {
    display: 'block',
    width: '100%',
    whiteSpace: 'normal',
    lineHeight: 20,
  },
  dismissal: {
    position: 'absolute',
    top: 18,
    right: 24,
    width: 16,
    height: 16,
    padding: 0,
    '.ms-Button-icon': {
      color: theme.semanticColors?.textQuaternary,
      fontSize: FontSizes.small,
    },
  },
  actions: {
    marginRight: 24,
    alignItems: 'start',
    paddingTop: 10,
  },
});

export const messageBarStylesNotification = (theme: IH2OTheme): Partial<IMessageBarStyles> => ({
  root: {
    borderRadius: 0,
    boxShadow: 'none',
    backgroundColor: theme.semanticColors?.contentBackground,
    ':hover': {
      backgroundColor: theme.semanticColors?.bodyBackground,
    },
    '&.has-actions': {
      '.ms-MessageBar-text': {
        marginBottom: 50,
      },
    },
    '.ms-MessageBar-actionsSingleLine': {
      position: 'absolute',
      right: 0,
      bottom: 8,
      marginRight: Sizes.padding,
    },
    '&.has-dismissal-icon': {
      '.ms-MessageBar-actionsSingleLine': {
        marginRight: Sizes.padding,
      },
      '.h2o-MessageBarContent-headerSecondContent': {
        marginRight: '24px',
      },
    },
  },
  content: {
    marginLeft: '8px',
  },
  iconContainer: {
    marginTop: '8px',
    marginLeft: '8px',
  },
  text: {
    margin: '8px',
    marginRight: '16px !important',
  },
  dismissal: {
    top: '10px',
    right: '16px',
  },
});

export interface IMessageBarContainerStyles {
  root?: IStyle;
  loader?: IStyle;
  count?: IStyle;
}

export interface IMessageBarContainerClassnames {
  root: string;
  loader: string;
  count: string;
}

export const messageBarContainerStylesDefault = (theme: IH2OTheme): Partial<IMessageBarContainerStyles> => ({
  root: {
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
  },
  count: {
    position: 'absolute',
    top: -7,
    right: -7,
    background: theme.semanticColors?.bodyBackground,
    color: theme.semanticColors?.textSecondary,
    fontWeight: FontWeights.bold,
    borderRadius: 9,
    minWidth: 18,
    height: 18,
    textAlign: 'center',
    lineHeight: 14,
    fontSize: FontSizes.xxsmall,
    padding: 2,
    boxShadow: BoxShadow,
  },
});

export const messageBarContainerStylesNotification: Partial<IMessageBarContainerStyles> = {
  count: {
    right: 0,
  },
};

export const messageBarContainerStylesError = (theme: IH2OTheme): Partial<IMessageBarContainerStyles> => ({
  count: {
    background: theme.semanticColors?.messageBarIconError,
    color: 'white',
  },
});

export const messageBarContainerStylesSuccess = (theme: IH2OTheme): Partial<IMessageBarContainerStyles> => ({
  count: {
    background: theme.semanticColors?.messageBarIconSuccess,
    color: 'white',
  },
});

export const messageBarContainerStylesInfo = (theme: IH2OTheme): Partial<IMessageBarContainerStyles> => ({
  count: {
    background: theme.semanticColors?.messageBarIconInfo,
    color: 'white',
  },
});

export const messageBarContainerStylesWarning = (theme: IH2OTheme): Partial<IMessageBarContainerStyles> => ({
  count: {
    background: theme.semanticColors?.messageBarIconWarning,
    color: 'white',
  },
});

export interface IMessageBarContentStyles {
  root?: IStyle;
  header?: IStyle;
  headerContent?: IStyle;
  headerSecondContent?: IStyle;
  title?: IStyle;
  subTitle?: IStyle;
  body?: IStyle;
}

export interface IMessageBarContentClassNames {
  root: string;
  header: string;
  headerContent: string;
  headerSecondContent: string;
  title: string;
  subTitle: string;
  body: string;
}

export const messageBarContentStylesDefault = (theme: IH2OTheme): Partial<IMessageBarContentStyles> => ({
  root: {
    '&.read': {
      '.h2o-MessageBarContent-title': {
        fontWeight: FontWeights.normal,
      },
    },
  },
  header: { display: 'flex' },
  headerContent: { flex: 1 },
  headerSecondContent: { color: theme.semanticColors?.textQuinary, fontSize: FontSizes.xxsmall },
  title: {
    color: theme.semanticColors?.messageBarTitleText,
    fontSize: FontSizes.large,
    fontWeight: FontWeights.semiBold,
    marginBottom: 16,
  },
  subTitle: {
    color: theme.semanticColors?.messageBarTitleText,
    fontSize: FontSizes.xsmall,
    fontWeight: FontWeights.medium,
    marginBottom: 8,
  },
  body: {
    color: theme.semanticColors?.messageBarContentText,
    fontSize: FontSizes.xsmall,
    fontWeight: FontWeights.medium,
  },
});

export const messageBarContentStylesNotification = (theme: IH2OTheme): Partial<IMessageBarContentStyles> => ({
  title: {
    fontSize: FontSizes.xsmall,
    marginBottom: 8,
  },
  body: {
    color: theme.semanticColors?.textQuinary,
    fontSize: FontSizes.xxsmall,
  },
});
